import { Navigate, RouteObject } from 'react-router-dom';

import { useAuthContext } from '@auth_module/context/AuthContext';
import { ProtectedRoute } from '@app/components/ProtectedRoute';
import { InsightsProvider } from '@insights_module/context/InsightsContext';
import { ViewsProvider } from '@insights_module/context/ViewsContext';
import { FiltersProvider } from '@insights_module/context/FiltersContext';
import { MegaphoneIntegrationPage } from '@insights_module/pages/integrations/MegaphoneIntegrationPage';
import { FEATURE_FLAG, useFeatureFlag } from '@auth_module/hooks/useFeatureFlag';
import { SegmentCreatorProvider } from '@insights_module/context/SegmentCreatorContext';
import { IHMIntegrationPage } from '@insights_module/pages/integrations/IHMIntegrationPage';
import { IntegrationsProvider } from '@insights_module/context/IntegrationsContext';
import { MainTableProvider } from '@insights_module/context/MainTableContext';
import { OpenAIProvider } from '@insights_module/context/OpenAIContext';
import { ShowDetailsProvider } from '@insights_module/context/ShowDetailsContext';
import { useUser } from '@auth_module/hooks/useUser';
import { MyLibraryProvider } from '@insights_module/context/MyLibraryContext';
import { OverviewPage } from '@insights_module/pages/OverviewPage';
import { OverviewProvider } from '@insights_module/context/OverviewContext';
import { LayoutProvider } from '@insights_module/context/LayoutContext';
import { MyLibraryPage } from '@insights_module/pages/MyLibraryPage';

export const _insightsRoutes = (): RouteObject[] => {
  const { auth } = useAuthContext();
  const { organization } = useUser();
  const { isEnabled: isMegaphoneIntegrationEnabled } = useFeatureFlag(
    FEATURE_FLAG.MEGAPHONE_INTEGRATION
  );
  const { isEnabled: isIHMStarshipIntegrationEnabled } = useFeatureFlag(
    FEATURE_FLAG.IHM_STARSHIP_INTEGRATION
  );
  const { isEnabled: isInsightsMyLibraryPageEnabled } = useFeatureFlag(
    FEATURE_FLAG.INSIGHTS_MY_LIBRARY_PAGE
  );
  const { isEnabled: isInsightsOverviewPageEnabled } = useFeatureFlag(
    FEATURE_FLAG.INSIGHTS_OVERVIEW_PAGE
  );

  return [
    {
      path: 'insights',
      element: (
        <InsightsProvider>
          <LayoutProvider>
            <MainTableProvider>
              <SegmentCreatorProvider>
                <ShowDetailsProvider>
                  <IntegrationsProvider>
                    <ViewsProvider>
                      <OpenAIProvider>
                        <FiltersProvider>
                          <ProtectedRoute
                            redirectPath='login'
                            isAllowed={
                              !!auth.token &&
                              organization?.type !== 'customer-free' &&
                              organization?.type !== 'customer'
                            }
                          />
                        </FiltersProvider>
                      </OpenAIProvider>
                    </ViewsProvider>
                  </IntegrationsProvider>
                </ShowDetailsProvider>
              </SegmentCreatorProvider>
            </MainTableProvider>
          </LayoutProvider>
        </InsightsProvider>
      ),
      children: [
        {
          index: true,
          element: (
            <ProtectedRoute
              redirectPath='/insights/shows'
              isAllowed={isInsightsOverviewPageEnabled}>
              <OverviewProvider>
                <OverviewPage />
              </OverviewProvider>
            </ProtectedRoute>
          ),
        },
        {
          path: '/insights/shows',
          element: (
            <ProtectedRoute redirectPath='/insights' isAllowed={isInsightsMyLibraryPageEnabled}>
              <MyLibraryProvider tableType='groups'>
                <MyLibraryPage />
              </MyLibraryProvider>
            </ProtectedRoute>
          ),
        },
        {
          path: '/insights/episodes',
          element: (
            <ProtectedRoute redirectPath='/insights' isAllowed={isInsightsMyLibraryPageEnabled}>
              <MyLibraryProvider tableType='audios'>
                <MyLibraryPage />
              </MyLibraryProvider>
            </ProtectedRoute>
          ),
        },
        {
          path: '/insights/integrations/megaphone',
          element: (
            <ProtectedRoute redirectPath='/insights' isAllowed={isMegaphoneIntegrationEnabled}>
              <MegaphoneIntegrationPage />
            </ProtectedRoute>
          ),
        },
        {
          path: '/insights/integrations/triton',
          element: (
            <ProtectedRoute redirectPath='/insights' isAllowed={isIHMStarshipIntegrationEnabled}>
              <IHMIntegrationPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'insights/*',
          element: <Navigate to='/insights' />,
        },
      ],
    },
  ];
};
